"use client";

import { memo, useEffect, useState } from "react";
import { HTTPError } from "ky";
import { Typography } from "@/components/ui/typography";
import Input from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Icon from "@/components/ui/Icon";
import {
  createUser,
  userExists,
  type UserExistsResponse,
} from "@/api/userRelations";
import { login, type AuthErrorBody } from "@/api/auth";
import { getAuthToken, setAuthToken } from "@/api/ky";
import { pushSignUpEvent } from "@/utils/gtm";
import { ROUTES } from "@/constants/routes";

// PROD-2026-05-18-011 — post-purchase account creation card.
//
// Renders below the success header on /order/confirmation and gives
// the just-paid guest customer a one-step path to claim a dashboard
// account. Soft blue surface keeps it visually recommended without
// blocking customers who want to skip.
//
// Three render states (real backend wired):
//
//   1. Default — soft blue card, password field, primary
//      "Access My Dashboard" CTA. Picked when the email isn't
//      already attached to a Languex account with a password set.
//
//   2. Existing account — when getUserByEmail returns an active
//      account, OR when the customer was already logged in at
//      checkout time (authToken present in localStorage). Hides the
//      form and surfaces a "Log in to Dashboard" deep-link instead
//      of letting the customer set a second password.
//
//   3. Success — replaces the card after a successful submit or
//      after the customer chooses "Create account later". Confirms
//      what happened and what the next step looks like.
//
// Cross-domain auth note:
//   The marketing site (languex.co) and the customer dashboard
//   (app.languex.co) don't share a localStorage namespace, so the
//   access_token we set here on languex.co won't carry over. After
//   a successful register + login we still redirect the customer to
//   the dashboard sign-in URL with their email pre-filled so they
//   land one re-type away from their order. The token we cached
//   here remains useful for any subsequent same-domain API calls
//   from the marketing site (e.g. quote follow-ups) without forcing
//   a fresh login.

export type AccountCreationCardProps = {
  firstName: string;
  lastName: string;
  email: string;
  orderId?: string | null;
  emphasis?: "default" | "hero";
} & React.ComponentProps<"div">;

// Spec r7 — final bullet wording. Each benefit is still a full
// noun-verb phrase (no vague labels per r6 spec) but trimmed of
// the connective filler that crept in during r6 ("anytime",
// "message", "from one dashboard"). The 4 items now sit cleanly
// on two rows in the 2-col desktop grid without wrapping.
const BENEFITS = [
  "Track translation progress",
  "Access completed translations",
  "Request revisions or support",
  "Manage all orders in one place",
];

type CardStatus =
  | "loading"
  | "idle"
  | "submitting"
  | "saved-created"
  | "saved-skipped";

const dashboardSignInUrl = (email: string, orderId?: string | null) => {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (orderId) params.set("orderId", orderId);
  params.set("from", "post-purchase");
  return `${ROUTES.languexSignIn}?${params.toString()}`;
};

const AccountCreationCard = ({
  firstName,
  lastName,
  email,
  orderId,
  className,
  emphasis = "default",
}: AccountCreationCardProps) => {
  const [mail, setMail] = useState(email);
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<CardStatus>("loading");
  const [existingAccount, setExistingAccount] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasEmailFromCheckout = Boolean(email && email.includes("@"));
  const isHero = emphasis === "hero";

  // Preflight: check if the order email already belongs to a
  // Languex account with a password set. If so, swap to the
  // existing-account variant before the customer sees the form so
  // we don't ask them to set a second password.
  //
  // Also early-out when the customer is already logged in
  // (authToken in localStorage) — common for repeat customers who
  // signed in during checkout. The order is already attached to
  // their account by the backend, so the card just needs to say so.
  //
  // Fail-open with a 2.5s hard timeout: if the API hangs or errors,
  // we render the default form so the customer is never stuck on a
  // loading skeleton. Better to occasionally ask for a password the
  // customer doesn't strictly need than to block account creation
  // on a flaky backend.
  useEffect(() => {
    const alreadyLoggedIn = Boolean(getAuthToken());
    if (alreadyLoggedIn) {
      setExistingAccount(true);
      setStatus("idle");
      return;
    }

    if (!hasEmailFromCheckout) {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    const PREFLIGHT_TIMEOUT_MS = 2500;
    (async () => {
      try {
        // PROD-2026-05-21-bug1 — was getUserByEmail (JWT-protected,
        // 401'd for guest customers, silently fell through to the
        // default form for every Scenario C visitor). Swapped to
        // the public /user/exists endpoint which returns just
        // { exists, user_id, has_password } with no PII so the
        // guest can correctly land on the existing-account variant.
        const res = (await Promise.race([
          userExists(email),
          new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), PREFLIGHT_TIMEOUT_MS)
          ),
        ])) as UserExistsResponse | null;
        if (cancelled) return;
        // Treat as "existing account" when a user record exists and
        // carries a password hash. Guest records created during
        // checkout don't have a password yet — those customers
        // should still get the password form so they can activate.
        const hasUserWithPassword = Boolean(res?.exists && res?.has_password);
        setExistingAccount(hasUserWithPassword);
      } catch {
        // Swallow — fall through to the default form.
      } finally {
        if (!cancelled) setStatus("idle");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [email, hasEmailFromCheckout]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);

    if (existingAccount) {
      // Form isn't rendered in this state; CTA is a link.
      return;
    }

    const submitEmail = (hasEmailFromCheckout ? email : mail).trim();
    if (!submitEmail || !submitEmail.includes("@")) {
      setErrorMessage("Enter a valid email address.");
      return;
    }
    if (!password || password.length < 8) {
      setErrorMessage("Use at least 8 characters for your password.");
      return;
    }

    setStatus("submitting");
    try {
      // source MUST be one of the explicit values the backend
      // _Users::add handles: "" (verify-by-email), "admin", or
      // "checkout". Any other value falls through every branch and
      // returns user_id: null with HTTP 200 — i.e. silently no-ops.
      // "checkout" is the right semantic match here: customer just
      // completed payment so the email is verified-by-Stripe, the
      // record is created with email_verified=1, and the existing
      // linkExistingOrdersToUser hook auto-attaches the just-paid
      // order to the new user_id.
      const created = await createUser({
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        email: submitEmail,
        password,
        role: 3,
        source: "checkout",
      });

      // Belt-and-braces: backend could 200 with a null user_id if
      // the source/role combination ever changes. Don't fire sign_up
      // or attempt login for a phantom account.
      if (!created?.user_id) {
        throw new Error(
          "Account creation didn't return a user id. Please try again."
        );
      }

      // Fire sign_up before the cross-domain hop so the event lands
      // in GTM (and downstream tags like GA4/server-side processing)
      // while we still own the languex.co context.
      pushSignUpEvent({
        email: submitEmail,
        first_name: firstName || "",
        last_name: lastName || "",
      });

      // Best-effort same-domain auto-login. Doesn't unlock the
      // dashboard (cross-domain localStorage) but keeps any
      // subsequent marketing-site API calls authenticated.
      try {
        const { access_token } = await login({
          email: submitEmail,
          password,
        });
        setAuthToken(access_token);
      } catch {
        // Non-fatal; the dashboard sign-in handles the real auth.
      }

      setStatus("saved-created");
      // Redirect to the dashboard sign-in with email + order id
      // context. Customer re-enters the password they just set
      // (fresh in working memory) and lands on their order. Short
      // delay lets the success card paint before the navigation so
      // there's no visual "snap".
      window.setTimeout(() => {
        window.location.href = dashboardSignInUrl(submitEmail, orderId);
      }, 800);
    } catch (err) {
      let message =
        "Couldn't create your account. Please try again or log in if you already have one.";
      let conflict = false;

      if (err instanceof HTTPError && err.response) {
        try {
          const body = (await err.response.json()) as AuthErrorBody;
          if (body?.messages?.error) {
            message = body.messages.error;
          }
        } catch {
          // Use default message.
        }
        // 409 / "already exists" — swap to existing-account state so
        // the customer sees a single clean path forward.
        if (
          err.response.status === 409 ||
          /exist|already/i.test(message)
        ) {
          conflict = true;
        }
      }

      if (conflict) {
        setExistingAccount(true);
        setErrorMessage(null);
      } else {
        setErrorMessage(message);
      }
      setStatus("idle");
    }
  };

  const handleSkip = () => {
    // No backend call: the guest order-confirmation email already
    // carries a "Create account" deep-link (shipped earlier with
    // the HMAC-signed download URLs), so the customer can claim
    // their dashboard from email whenever they want. We just dismiss
    // here so the card converts into a clean reassurance state.
    setStatus("saved-skipped");
  };

  // Loading state — keep it minimal. Fixed height roughly matches
  // the final card so the page doesn't jump when the preflight
  // resolves.
  if (status === "loading") {
    return (
      <div
        className={cn(
          "rounded-[18px] border border-blue-200 bg-blue-50 p-4 md:p-6 min-h-[200px]",
          className
        )}
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-4 w-32 rounded bg-white/60 mb-4 animate-pulse" />
        <div className="h-6 w-2/3 rounded bg-white/60 mb-3 animate-pulse" />
        <div className="h-3 w-full rounded bg-white/60 mb-2 animate-pulse" />
        <div className="h-3 w-5/6 rounded bg-white/60 animate-pulse" />
      </div>
    );
  }

  if (status === "saved-created" || status === "saved-skipped") {
    const isCreated = status === "saved-created";
    return (
      <div
        className={cn(
          "rounded-[18px] border border-green-300 bg-green-50 p-4 md:p-6 text-semantic-text-primary",
          className
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100">
            <Icon
              id="icon-check_20"
              width={20}
              height={20}
              className="text-green-500"
            />
          </span>
          <div className="grid gap-1">
            <Typography variant="titleSm" className="font-semibold">
              {isCreated
                ? "Account created — taking you to your dashboard"
                : "No problem — your order is safe"}
            </Typography>
            {/*
              Created copy: cross-domain auth means we can't carry
              the session token across to app.languex.co, so we set
                expectations honestly — sign in once with the password
                they JUST set (fresh in working memory). Way less
                confusing than a silent "why am I being asked to sign
                in again?" moment on the dashboard.

              Skipped copy: the order confirmation email goes out at
              order-creation time on the backend and already carries
              the "Create Your Account" CTA from
              CheckoutFlowEmailService::buildCreateAccountCTAHtml.
              We point them at that email rather than promising a new
              one we don't send.
            */}
            <Typography
              variant="labelSm"
              className="font-normal text-semantic-text-primary"
            >
              {isCreated ? (
                <>
                  Sign in on the next page with the password you just
                  created for{" "}
                  <span className="font-semibold break-all">
                    {mail || email || "your email"}
                  </span>
                  .
                </>
              ) : (
                <>
                  Your translation is on track. When you&apos;re ready to
                  claim your dashboard, tap the{" "}
                  <span className="font-semibold">Create Your Account</span>{" "}
                  link in your order confirmation email
                  {email ? (
                    <>
                      {" "}sent to{" "}
                      <span className="font-semibold break-all">{email}</span>
                    </>
                  ) : null}
                  .
                </>
              )}
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // PROD-2026-05-19 r5: soft blue surface using the design-
        // system tokens (--blue-50 bg, --blue-200 border). Lifts off
        // the page enough to read as the recommended next step
        // without the heavier "hero" ring + shadow treatment that
        // Layout 3 uses for the high-emphasis variant.
        "rounded-[18px] border bg-blue-50 p-4 md:p-6 text-semantic-text-primary",
        isHero
          ? "border-blue-200 shadow-[0_4px_24px_-8px_rgba(47,128,237,0.18)] ring-1 ring-blue-200"
          : "border-blue-200 shadow-[0_2px_16px_-8px_rgba(47,128,237,0.12)]",
        className
      )}
    >
      {/*
        Inner gap tightened from gap-5 to gap-4 per r6 spec.
        Combined with the form-field removal and 2-col benefits,
        the account card now lands "Access My Dashboard" above the
        fold on a 16-inch MacBook viewport at default zoom.
      */}
      <div className="grid gap-4">
        <div className="grid gap-2">
          {/*
            r9: "Recommended" badge only renders on the create-account
            variant, where the customer has a real choice between
            "Access My Dashboard" and "Create account later" and the
            badge is steering them toward the conversion path.
            On the existing-account variant there is no alternative
            action (no skip link, single CTA), so the badge is
            meaningless and was making the headline feel like it
            was selling something rather than acknowledging the
            customer's existing relationship with us.
          */}
          {!existingAccount && (
            <span
              className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-600 ring-1 ring-blue-200"
            >
              <Icon id="icon-smart-hint_24" width={12} height={12} />
              Recommended
            </span>
          )}
          <Typography
            variant={isHero ? "titleLg" : "titleMd"}
            className="font-semibold text-semantic-text-primary lg:text-2xl lg:leading-[130%]"
            asChild
          >
            {/*
              r9: distinct headlines per variant so the customer
              instantly understands which path they're on. The
              create-account headline ("Your dashboard is ready")
              implies first-time setup, which is exactly wrong for
              a returning customer who's had a dashboard for months.
              The existing-account headline says "Welcome back" and
              optionally personalizes with firstName from the URL.
            */}
            <h3>
              {existingAccount
                ? firstName
                  ? `Welcome back, ${firstName}`
                  : "Welcome back"
                : "Your dashboard is ready"}
            </h3>
          </Typography>
          {/*
            Spec r7: subtitle paragraph dropped for the default
            state — the 4-bullet grid below carries the value, and
            removing the paragraph gives the heading + badge more
            breathing room while shaving ~30px of vertical height.
            r9: existing-account subline rewritten to reference the
            specific order ID (concrete > generic) and tell the
            customer exactly what sign-in unlocks for them.
          */}
          {existingAccount && (
            <Typography
              variant="labelMd"
              className="font-normal text-semantic-text-primary"
            >
              {orderId
                ? `Order #${orderId} is linked to your Languex account. Sign in to track progress and access your translation.`
                : "Your new order is linked to your Languex account. Sign in to track progress and access your translation."}
            </Typography>
          )}
        </div>

        {/*
          2-col benefits grid per r6 spec. Halves the vertical
          height of the four-bullet section on desktop while
          keeping full-sentence wording (no truncation). Stays a
          single column on mobile where horizontal space is tight.
          r9: grid is hidden on the existing-account variant — the
          customer already uses these features daily, so listing
          them as "benefits" reads as selling something they
          already own. The single reassurance pill below carries
          all the context the existing-account customer needs.
        */}
        {!existingAccount && (
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-x-6 md:gap-y-2">
            {BENEFITS.map((label, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[14px] font-medium leading-snug text-semantic-text-primary"
              >
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-blue-200">
                  <Icon
                    id="icon-check_20"
                    width={10}
                    height={10}
                    className="text-blue-600"
                  />
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleSubmit} className="grid gap-3">
          {existingAccount ? (
            // Existing-account state: skip the name/email/password
            // capture entirely. The customer already has the account;
            // their email is enough to surface a deep-link.
            //
            // Backend: Account::authenticate now calls
            // OrderLinkingHelper::linkOrphanOrdersByEmail right after
            // a successful login (commit 9000666 on lx-app-be), so
            // the just-paid order — even when checkout was completed
            // as guest with a matching email — is attached to the
            // user_id before the dashboard's orders query runs.
            //
            // r9: pill copy pre-empties the only real anxiety this
            // customer has — "do I need to set / reset a password?"
            // The headline + subline already cover what action to
            // take; the pill confirms the password situation.
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-[13px] font-medium leading-snug text-semantic-text-primary">
              <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-50 ring-1 ring-blue-200">
                <Icon
                  id="icon-check_20"
                  width={10}
                  height={10}
                  className="text-blue-600"
                />
              </span>
              <span>
                No new password needed — use the password you already
                have for Languex.
              </span>
            </div>
          ) : (
            <>
              {/*
                Spec r6: don't re-ask for name / email when we
                already have them from the order payload. A single
                context line ("Account will be linked to: ...") keeps
                the customer oriented and saves ~120px of form
                height. Fallback to a real email input only when
                the email is missing — rare but possible if checkout
                returns to the confirmation page without the email
                query param populated.
              */}
              {hasEmailFromCheckout ? (
                // Mobile fix r8: email span gets break-all so long
                // addresses (firstname.lastname@verylongdomain.com)
                // don't push past the pill on narrow phones.
                <div className="rounded-md border border-blue-200 bg-white px-3 py-2 text-[13px] font-medium leading-snug text-semantic-text-primary">
                  Account will be linked to:{" "}
                  <span className="font-semibold break-all">{email}</span>
                </div>
              ) : (
                <div className="grid gap-1">
                  <label
                    htmlFor="acct-email"
                    className="text-[13px] font-medium text-semantic-text-primary"
                  >
                    Email
                  </label>
                  <Input
                    id="acct-email"
                    type="email"
                    placeholder="you@example.com"
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
              )}
              <div className="grid gap-1">
                <label
                  htmlFor="acct-password"
                  className="text-[13px] font-medium text-semantic-text-primary"
                >
                  Create a password
                </label>
                <Input
                  id="acct-password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <p className="text-[12px] font-normal text-semantic-text-secondary">
                  Use at least 8 characters.
                </p>
              </div>
            </>
          )}

          {errorMessage && (
            <p className="text-[12px] font-medium text-error" role="alert">
              {errorMessage}
            </p>
          )}

          {/*
            CTA stack per r6 spec: primary full-width and visually
            dominant, secondary moved BELOW (not side-by-side) and
            rendered as a quieter text link with reassurance copy
            underneath. Side-by-side layout from r5 added a tiny
            amount of height saving but made the skip link feel
            like a sibling action competing with the primary.
            Stacking makes the primary unmistakeable and lets the
            skip live in its own quiet column.
          */}
          <div className="mt-1 grid gap-3">
            {existingAccount ? (
              <Button asChild variant="primary" className="h-12 w-full">
                <a
                  href={dashboardSignInUrl(email || mail, orderId)}
                  rel="noopener"
                >
                  Log in to Dashboard
                </a>
              </Button>
            ) : (
              <>
                <Button
                  type="submit"
                  variant="primary"
                  className="h-12 w-full"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? "Saving…" : "Access My Dashboard"}
                </Button>
                <div className="grid gap-1 text-center">
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="text-[13px] font-medium text-semantic-text-secondary underline decoration-dotted underline-offset-2 hover:text-blue-600"
                    disabled={status === "submitting"}
                  >
                    Create account later
                  </button>
                  <p className="text-[12px] font-normal text-semantic-text-secondary mx-auto max-w-[420px]">
                    Your translation is on track either way. You can
                    claim your dashboard later from the{" "}
                    <span className="font-medium">Create Your Account</span>{" "}
                    link in your order confirmation email.
                  </p>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default memo(AccountCreationCard);
